import React from "react";
import { motion } from "framer-motion";

// MUI COMPONENTS
import {
  alpha,
  Avatar,
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

// ICONS
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import LocalBarIcon from "@mui/icons-material/LocalBar";
import {
  AnimatedModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "../../modals/AnimatedModal"; // Import drink icon

/**
 * DrinkConfirmationDialog - A simplified dialog that confirms special handling for drinks
 *
 * @param {boolean} isOpen - Controls visibility of the dialog
 * @param {function} onClose - Function to call when closing the dialog without confirmation
 * @param {function} onConfirm - Function to call when confirming the dialog
 * @returns {JSX.Element} - The rendered component
 */
const DrinkConfirmationDialog = ({ isOpen, onClose, onConfirm }) => {
  const theme = useTheme();

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={onClose}
      width="400px"
      maxWidth="95%"
      zIndex={11000}
      forceOnTop={true}
    >
      <ModalHeader
        title="Atenção: Bebidas"
        icon={LocalBarIcon}
        onClose={onClose}
        sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1) }}
      />

      <ModalContent>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
              backgroundColor: alpha(theme.palette.warning.main, 0.05),
            }}
          >
            <Stack
              spacing={2}
              direction="row"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.warning.main, 0.2),
                  color: theme.palette.warning.main,
                }}
              >
                <LocalBarIcon />
              </Avatar>
              <Typography variant="h6" fontWeight={600}>
                Cuidado com Bebidas
              </Typography>
            </Stack>

            <Typography variant="body2" sx={{ mb: 1 }}>
              Este pedido contém bebidas que requerem cuidados especiais no
              transporte:
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
              <CheckCircleIcon
                fontSize="small"
                color="success"
                sx={{ mr: 1 }}
              />
              <Typography variant="body2">
                Manter sempre na posição vertical
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </ModalContent>

      <ModalFooter>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Button
                fullWidth
                variant="outlined"
                color="inherit"
                onClick={onClose}
                startIcon={<KeyboardBackspaceIcon />}
                sx={{ height: 42 }}
              >
                Voltar
              </Button>
            </motion.div>
          </Grid>
          <Grid item xs={6}>
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Button
                fullWidth
                variant="contained"
                color="warning"
                onClick={onConfirm}
                endIcon={<CheckCircleIcon />}
                sx={{ height: 42 }}
              >
                Confirmar
              </Button>
            </motion.div>
          </Grid>
        </Grid>
      </ModalFooter>
    </AnimatedModal>
  );
};

export default DrinkConfirmationDialog;
